import { useTheme } from '@emotion/react';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet';

import { processFilestackUrl } from '@jebel/utils';

import { LOCAL_STORAGE_SELECTED_COMMUNITY } from 'shared/constants';
import { CurrentCommunityFragment, useCurrentCommunityQuery } from 'shared/graphql';
import { useLocalStorageState, useQueryParams } from 'shared/hooks';
import { recordDebug, recordError, recordEvent } from 'shared/utils/record';

const SERVER_URL = process.env.REACT_APP_ENDPOINT;

export interface CommunityProvidedContext {
  /**
   * The currently selected community.
   * If no community is selected, this will be `null`.
   * If a community is selected, this will be an object with the `id` of the selected community.
   */
  selectedCommunity: CurrentCommunityFragment | null;

  /**
   * Select a community by `id`.
   * @param id The `id` of the community to select.
   */
  selectCommunity(id: string): Promise<void>;

  /**
   * Whether the community is currently loading.
   */
  loading: boolean;
}

const CommunityContext = createContext<CommunityProvidedContext | null>(null);

interface Params {
  /** @deprecated Use `community` instead. */
  school?: string;
  /** ID of the desired community to be selected. */
  community?: string;
}

function useCreateCommunityContext(): CommunityProvidedContext {
  const [params] = useQueryParams<Params>();

  const [selected, setSelected] = useLocalStorageState<CurrentCommunityFragment>(
    LOCAL_STORAGE_SELECTED_COMMUNITY,
  );

  const selectedCommunityID = useMemo(
    () => params?.community ?? params.school ?? selected?.id,
    [params, selected],
  );

  const {
    data: response,
    loading,
    refetch,
  } = useCurrentCommunityQuery({
    skip: !selectedCommunityID,
    variables: { id: selectedCommunityID! },
    // This will ignore errors in the query, so we can handle them gracefully.
    errorPolicy: 'ignore',
    // Prevents to refetch the query if the data is already in the cache.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',

    onCompleted({ community }) {
      if (!community) {
        return;
      }

      recordDebug(`Selecting community "${community.id}" loaded from remote`);
      // Update the local storage with the latest data.
      setSelected(community);
    },
  });

  const selectedCommunity = useMemo(() => {
    return response?.community ?? selected ?? null;
  }, [response, selected]);

  const selectCommunity = async (id: string): Promise<void> => {
    if (id === selectedCommunity?.id) {
      return;
    }

    const { data: response } = await refetch({ id });

    if (!response?.community) {
      recordError(`Unable to select community "${id}", not found`);
      return;
    }

    setSelected(response.community);
    recordEvent('Select Community', {
      'Community ID': id,
      'Community Name': response.community.name!,
    });
  };

  return {
    selectedCommunity,
    selectCommunity,

    loading,
  };
}

const FAVICON_SIZE = 32;
const TOUCH_ICON_SIZE = 192;

export function CommunityProvider(props: PropsWithChildren<unknown>) {
  const theme = useTheme();
  const context = useCreateCommunityContext();

  const community = useMemo(() => context.selectedCommunity, [context]);

  const faviconURL = useMemo(() => {
    const source = community?.images?.browserLogo?.downloadUrl;

    if (!source) {
      return '/favicon.ico';
    }

    return processFilestackUrl(source, {
      resize: { width: FAVICON_SIZE, height: FAVICON_SIZE },
    });
  }, [community]);

  const touchIconURL = useMemo(() => {
    const source = community?.images?.browserLogo?.downloadUrl;

    if (!source) {
      return '/favicon.ico';
    }

    return processFilestackUrl(source, {
      resize: { width: TOUCH_ICON_SIZE, height: TOUCH_ICON_SIZE },
    });
  }, [community]);

  const manifestURL = useMemo(() => {
    if (!community) {
      // Return the fallback manifest URL.
      return '/manifest.json';
    }

    // Return the community-specific manifest URL.
    // https://github.com/jebelapp/jebel/issues/1658
    return `${SERVER_URL}/webhook/school/${community.id}/manifest`;
  }, [community]);

  return (
    <CommunityContext.Provider value={context}>
      <Helmet>
        <title>{community?.fullName ?? community?.name ?? 'Jebel'}</title>

        <meta name="theme-color" content={theme.palette.primary.main} />

        <link rel="manifest" href={manifestURL} />

        <link rel="icon" href={faviconURL} />
        <link rel="apple-touch-icon" href={touchIconURL} />
      </Helmet>

      {props.children}
    </CommunityContext.Provider>
  );
}

/**
 * Custom hook to access the `CommunityContext`.
 *
 * This hook provides the current value of the `CommunityContext`. It must be used
 * within a component that is a descendant of a {@linkcode CommunityProvider}. If used outside
 * of a {@linkcode CommunityProvider}, it will throw an error.
 *
 * @throws {Error} If the hook is used outside of a {@linkcode CommunityProvider}.
 * @returns The current context value of the `CommunityContext`.
 */
export function useCommunityContext() {
  const context = useContext(CommunityContext);

  if (!context) {
    throw new Error(`"useCommunityContext" must be used within a "CommunityProvider"`);
  }

  return context;
}
